import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';

export type OtpMode = 'login' | 'forgot-password';

@Component({
    selector: 'app-login-with-otp',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSelectModule
    ],
    templateUrl: './login-with-otp.component.html',
    styleUrl: './login-with-otp.component.scss'
})
export class LoginWithOtpComponent implements OnInit {
  @Input() mode: OtpMode = 'login';
  @Output() switchToRegular = new EventEmitter<void>();
  @Output() passwordResetSuccess = new EventEmitter<{mobile: string}>();
  
  otpForm: FormGroup;
  isLoading = false;
  isOtpSent = false;
  countdown = 0;
  countdownInterval: any;

  countryCodes = [
    { code: '+91', country: 'India' },
    { code: '+1', country: 'USA' },
    { code: '+44', country: 'UK' },
    { code: '+61', country: 'Australia' },
    { code: '+86', country: 'China' },
    { code: '+81', country: 'Japan' }
  ];

  constructor(private fb: FormBuilder) {
    this.otpForm = this.fb.group({
      countryCode: ['+91', [Validators.required]],
      mobileNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      otp: ['', [Validators.required, Validators.pattern(/^[0-9]{4}$/)]]
    });
  }

  ngOnInit() {
    // Initialize component
  }

  get isForgotPasswordMode(): boolean {
    return this.mode === 'forgot-password';
  }

  get formTitle(): string {
    return this.isForgotPasswordMode ? 'Reset Password' : 'Login with OTP';
  }

  get backButtonText(): string {
    return this.isForgotPasswordMode ? 'Back to Login' : 'Back to Login';
  }

  get sendOtpButtonText(): string {
    return this.isForgotPasswordMode ? 'Send Reset OTP' : 'Send OTP';
  }

  get verifyOtpButtonText(): string {
    return this.isForgotPasswordMode ? 'Verify & Reset Password' : 'Verify OTP';
  }

  get instructionText(): string {
    return this.isForgotPasswordMode 
      ? 'You will receive a One-Time Password (OTP) on your registered mobile number to reset your password. A new password will be sent to your mobile number after verification.'
      : 'You will receive a One-Time Password (OTP) on your registered mobile number for verification.';
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.otpForm.get(fieldName);
    return field ? (field.invalid && (field.dirty || field.touched)) : false;
  }

  getFieldError(fieldName: string): string {
    const field = this.otpForm.get(fieldName);
    
    if (!field || !field.errors) return '';
    
    if (field.errors['required']) {
      return `${fieldName === 'mobileNumber' ? 'Mobile Number' : fieldName.toUpperCase()} is required`;
    }
    
    if (field.errors['pattern']) {
      if (fieldName === 'mobileNumber') {
        return 'Please enter a valid 10-digit mobile number';
      }
      if (fieldName === 'otp') {
        return 'Please enter a valid 4-digit OTP';
      }
    }
    
    return 'Invalid input';
  }

  onSendOtp() {
    if (this.otpForm.get('mobileNumber')?.valid) {
      this.isLoading = true;
      // Simulate API call to send OTP
      setTimeout(() => {
        this.isLoading = false;
        this.isOtpSent = true;
        this.startCountdown();
        console.log('OTP sent to:', this.otpForm.get('mobileNumber')?.value);
      }, 2000);
    } else {
      this.otpForm.get('mobileNumber')?.markAsTouched();
    }
  }

  onVerifyOtp() {
    if (this.isForgotPasswordMode) {
      this.onVerifyOtpForPasswordReset();
    } else {
      this.onVerifyOtpForLogin();
    }
  }

  onVerifyOtpForLogin() {
    if (this.otpForm.get('otp')?.valid) {
      this.isLoading = true;
      // Simulate API call to verify OTP for login
      setTimeout(() => {
        this.isLoading = false;
        console.log('Login OTP verified:', this.otpForm.value);
        // Navigate to dashboard or show success message
      }, 2000);
    } else {
      this.otpForm.get('otp')?.markAsTouched();
    }
  }

  onVerifyOtpForPasswordReset() {
    if (this.otpForm.get('otp')?.valid) {
      this.isLoading = true;
      // Simulate API call to verify OTP and send password to mobile
      setTimeout(() => {
        this.isLoading = false;
        const mobile = this.otpForm.get('mobileNumber')?.value;
        console.log('Password reset OTP verified:', this.otpForm.value);
        console.log('New password sent to mobile:', mobile);
        this.passwordResetSuccess.emit({ mobile });
      }, 2000);
    } else {
      this.otpForm.get('otp')?.markAsTouched();
    }
  }

  startCountdown() {
    this.countdown = 30;
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(this.countdownInterval);
      }
    }, 1000);
  }

  resendOtp() {
    if (this.countdown <= 0) {
      this.onSendOtp();
    }
  }

  onBackToLogin() {
    // Emit event to switch back to regular login
    this.switchToRegular.emit();
  }

  ngOnDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }
} 